// Dashboard access configuration and utilities
// Defines which roles can access which dashboard views

// Role-based dashboard access matrix. The Costing view reads margins and costs, so it goes to the same roles as
// GET /api/costing/* on the backend (COSTING, MANAGER, ADMIN; #373/#376).
export const DASHBOARD_ACCESS = {
  ADMIN: ['MANAGER', 'SUPERVISOR', 'OPERATOR', 'PLANNER', 'WAREHOUSE', 'COSTING'],
  SUPERVISOR: ['MANAGER', 'OPERATOR', 'PLANNER'],
  MANAGER: ['COSTING'],
  COSTING: ['COSTING']
  // Other roles don't have cross-dashboard access
};

// Available dashboard options with configuration
export const DASHBOARD_OPTIONS = [
  {
    code: 'DEFAULT',
    name: 'Default Home',
    icon: '🏠',
    description: 'Your default dashboard',
    route: '/home'
  },
  {
    code: 'MANAGER',
    name: 'Manager View',
    icon: '👔',
    description: 'Team overview & performance metrics',
    route: '/home?dashboard=manager'
  },
  {
    code: 'SUPERVISOR',
    name: 'Supervisor View',
    icon: '👨‍💼',
    description: 'Production oversight & cost tracking',
    route: '/home?dashboard=supervisor'
  },
  {
    code: 'OPERATOR',
    name: 'Operator View',
    icon: '👷',
    description: 'Task management & work tracking',
    route: '/home?dashboard=operator'
  },
  {
    code: 'PLANNER',
    name: 'Planner View',
    icon: '📅',
    description: 'Production scheduling & resource allocation',
    route: '/home?dashboard=planner'
  },
  {
    code: 'WAREHOUSE',
    name: 'Warehouse View',
    icon: '📦',
    description: 'Inventory operations & stock management',
    route: '/home?dashboard=warehouse'
  },
  {
    code: 'COSTING',
    name: 'Costing View',
    icon: '💰',
    description: 'Cost analysis and pricing',
    route: '/home?dashboard=costing'
  }
];

// A user can have several roles (#285): these helpers take one role code or an array of
// role codes, and a dashboard is available if ANY of the roles grants it.
const asRoles = (userRoles) =>
  (Array.isArray(userRoles) ? userRoles : [userRoles]).filter(Boolean).map(r => r.toUpperCase());

const allowedDashboardsFor = (userRoles) => {
  const lists = asRoles(userRoles).map(r => DASHBOARD_ACCESS[r]).filter(Boolean);
  return lists.length ? [...new Set(lists.flat())] : null;
};

/**
 * Check if a user role has access to view a specific dashboard
 * @param {string|string[]} userRoles - The user's role(s) (ADMIN, SUPERVISOR, etc.)
 * @param {string} targetDashboard - The dashboard code to check access for
 * @returns {boolean} - True if user has access, false otherwise
 */
export const hasAccessToDashboard = (userRoles, targetDashboard) => {
  if (!asRoles(userRoles).length || !targetDashboard) return false;

  // Allow access to default dashboard for everyone
  if (targetDashboard === 'DEFAULT') return true;

  // Check if any user role has access to the target dashboard
  const allowedDashboards = allowedDashboardsFor(userRoles);
  return allowedDashboards ? allowedDashboards.includes(targetDashboard.toUpperCase()) : false;
};

/**
 * Get available dashboard options for a user's role(s)
 * @param {string|string[]} userRoles - The user's role(s)
 * @returns {Array} - Array of dashboard options the user can access
 */
export const getAvailableDashboards = (userRoles) => {
  const allowedDashboards = allowedDashboardsFor(userRoles);

  if (!allowedDashboards) {
    return [DASHBOARD_OPTIONS[0]]; // Only default for roles without cross-dashboard access
  }

  // Return default option plus allowed dashboards
  return [
    DASHBOARD_OPTIONS[0], // Default home
    ...DASHBOARD_OPTIONS.filter(option =>
      allowedDashboards.includes(option.code)
    )
  ];
};

/**
 * Check if a user is eligible for dashboard selection (any role with cross-dashboard access)
 * @param {string|string[]} userRoles - The user's role(s)
 * @returns {boolean} - True if user can select different dashboards
 */
export const canSelectDashboard = (userRoles) =>
  asRoles(userRoles).some(r => Object.prototype.hasOwnProperty.call(DASHBOARD_ACCESS, r));

/**
 * Get dashboard option by code
 * @param {string} code - The dashboard code
 * @returns {Object|null} - Dashboard option object or null if not found
 */
export const getDashboardByCode = (code) => {
  return DASHBOARD_OPTIONS.find(option =>
    option.code.toUpperCase() === code?.toUpperCase()
  ) || null;
};